/**
 * HistoryPanel — Verlauf section of the farm object view.
 *
 * One section, two clearly-labelled sub-feeds rendered only when
 * non-empty (fixes B2 — no contradictory empty-state above a populated
 * list): business events (deliveries, settlements, …) inline with
 * show-more, and the audit change log behind a disclosure.
 *
 * Uninformative pseudo-events whose title is just the raw type slug
 * ("parcel", bug B7) are dropped — the relations panel owns those rows.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { EntityEvent } from '@hofsync/design-system';
import type { EntitySourceDoc, LenseField } from '../../../services/twinService';
import { useFeatureFlag } from '../../../contexts/FeatureFlagContext';
import { useEntityHistory } from '../../../hooks/useTwin';
import { formatDateString } from '../formatValue';
import EntityHistoryPanel from '../EntityHistoryPanel';
import TransitionsLog from './TransitionsLog';
import './HistoryPanel.css';

const VISIBLE_LIMIT = 5;

export function isInformativeEvent(event: EntityEvent): boolean {
    const title = event.title?.trim();
    if (!title) return false;
    // A title equal to the event_type (or a bare snake_case slug) is the
    // reverse-ref scanner's placeholder, not a real event caption.
    if (title === event.event_type) return false;
    if (/^[a-z][a-z0-9_]*$/.test(title)) return false;
    return true;
}

function statusClass(event: EntityEvent): string {
    switch (event.status) {
        case 'completed':
            return 'history-panel__event--completed';
        case 'pending':
            return 'history-panel__event--pending';
        default:
            return 'history-panel__event--neutral';
    }
}

export interface HistoryPanelProps {
    events: EntityEvent[];
    eventsLoading?: boolean;
    entityType?: string;
    entityId?: string;
    /** Live twin mode — the history/series reads only fire when true, so
     * demo/mock object views never hit the live B-read API with mock ids
     * (mirrors the relations/docs/events ``live`` gate). */
    live?: boolean;
    /** Merged ontology schema — resolves attribute labels + numeric types
     * for the Änderungen transitions log / value-history view. */
    fields?: LenseField[];
    /** The card's value-label projection (HOF-696 D20) — the Änderungen log shows
     * the labels the card shows ("freigegeben", not ``enabled``). */
    valueLabels?: Record<string, Record<string, string>>;
    /** Linked source docs — resolves each transition's "Beleg <year>"
     * provenance label (never a raw id) for the log + value-history view. */
    sourceDocs?: EntitySourceDoc[];
    onPreviewDocument?: (docId: string) => void;
}

export const HistoryPanel: React.FC<HistoryPanelProps> = ({
    events,
    eventsLoading,
    entityType,
    entityId,
    live = true,
    fields,
    valueLabels,
    sourceDocs,
    onPreviewDocument,
}) => {
    const [showAll, setShowAll] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);
    const auditReadEnabled = useFeatureFlag('audit_events.read_path');

    // #1913 C — the append-only attribute transitions ("Änderungen").
    // UNGATED by feature flag (first-party product surface, unlike the
    // flag-gated raw Änderungsprotokoll below) but STILL behind the twin
    // ``live`` gate: a demo/mock object view must not call the live
    // B-read API with mock ids (codex round-1 P2).
    const { data: historyData, isLoading: historyLoading, isError: historyError } = useEntityHistory(
        live ? entityType : undefined,
        live ? entityId : undefined,
    );
    const transitions = historyData?.transitions ?? [];

    const docsById = useMemo(() => {
        const map = new Map<string, EntitySourceDoc>();
        for (const doc of sourceDocs ?? []) map.set(doc.document_id, doc);
        return map;
    }, [sourceDocs]);

    const informative = events
        .filter(isInformativeEvent)
        .sort((a, b) => b.event_date.localeCompare(a.event_date));
    const visible = showAll ? informative : informative.slice(0, VISIBLE_LIMIT);
    const hiddenCount = informative.length - visible.length;
    const canShowAudit = auditReadEnabled && !!entityType && !!entityId;
    const hasTransitions = transitions.length > 0;
    const hasMoreTransitions = historyData?.has_more ?? false;

    // The panel blanks ONLY while events load — the events/audit sections
    // must not wait on the slower /history B-read (codex round-2 P1). The
    // Änderungen section carries its OWN loading + empty handling below.
    if (eventsLoading) {
        return <p className="history-panel__none">Ereignisse werden geladen…</p>;
    }

    return (
        <div className="history-panel">
            {informative.length === 0 && !hasTransitions && !canShowAudit && !historyLoading && !historyError && (
                <p className="history-panel__none">Noch keine Ereignisse</p>
            )}

            {visible.map((event) => (
                <div key={event.event_id} className={`history-panel__event ${statusClass(event)}`}>
                    <span className="history-panel__event-date">{formatDateString(event.event_date)}</span>
                    <span className="history-panel__event-body">
                        {event.source_doc_id && onPreviewDocument ? (
                            <button
                                type="button"
                                className="history-panel__event-link"
                                onClick={() => onPreviewDocument(event.source_doc_id!)}
                            >
                                {event.title}
                            </button>
                        ) : (
                            <span className="history-panel__event-title">{event.title}</span>
                        )}
                        {event.lines.length > 0 && (
                            <span className="history-panel__event-lines">
                                {event.lines.map((l) => (
                                    <span key={l.line_id} className="history-panel__event-line">
                                        {l.product_name}
                                        {l.product_spec ? ` ${l.product_spec}` : ''}
                                        {l.quantity ? ` — ${l.quantity} ${l.quantity_unit}` : ''}
                                    </span>
                                ))}
                            </span>
                        )}
                        {event.description && (
                            <span className="history-panel__event-desc">{event.description}</span>
                        )}
                    </span>
                </div>
            ))}

            {hiddenCount > 0 && (
                <button type="button" className="history-panel__more" onClick={() => setShowAll(true)}>
                    <ChevronDown size={14} />
                    {hiddenCount} weitere anzeigen
                </button>
            )}

            {historyLoading && <p className="history-panel__none">Änderungen werden geladen…</p>}

            {/* Section-local error — a failed /history read must be honest,
                NOT masquerade as the empty "Noch keine Ereignisse" state,
                and must never blank the already-loaded events/audit above. */}
            {historyError && !historyLoading && (
                <p className="history-panel__error">Änderungen konnten nicht geladen werden.</p>
            )}

            {hasTransitions && entityType && entityId && (
                <TransitionsLog
                    transitions={transitions}
                    entityType={entityType}
                    entityId={entityId}
                    fields={fields}
                    valueLabels={valueLabels}
                    docsById={docsById}
                    onPreviewDocument={onPreviewDocument}
                />
            )}

            {hasMoreTransitions && (
                <p className="history-panel__more-note">
                    Weitere Änderungen vorhanden – nur die neuesten werden angezeigt.
                </p>
            )}

            {canShowAudit && (
                <div className="history-panel__audit">
                    <button
                        type="button"
                        className="history-panel__audit-toggle"
                        onClick={() => setAuditOpen((v) => !v)}
                    >
                        <ChevronDown
                            size={14}
                            className={`history-panel__audit-icon${auditOpen ? ' history-panel__audit-icon--open' : ''}`}
                        />
                        Änderungsprotokoll
                    </button>
                    {auditOpen && (
                        <div className="history-panel__audit-body">
                            <EntityHistoryPanel entityType={entityType!} entityId={entityId!} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HistoryPanel;
